import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {takeUntil} from "rxjs/operators";
import {map, Subject} from "rxjs";
import {IconType} from "@pb-labs/icon";
import {getDocIcon, getDocIconColor, getFileSize} from "@app/modules/browse/helpers";
import {TreeNode} from "@app/modules/models";
import {SortEvent, SortType} from "@app/modules/browse/components/browse-table/models";
import {compare} from "@app/modules/browse/components/browse-table/sortable.directive";
import {SortableService} from "@app/modules/browse/components/browse-table/sortable.service";
import {UserSettings} from "@app/services/sftp-api/sftp-api.service";
import {BrowseService} from "@app/modules/browse/services/browse.service";

const DEFAULT_TOTAL_PAGE_COUNT = 1;
const SEARCHED_FIELD_NAME = "name";
const FIRST_PAGE = 1;
const DEFAULT_SIZE = 25;

@Component({
  selector: "app-browse-table",
  templateUrl: "./browse-table.component.html",
  styleUrls: ["./browse-table.component.scss"],
})
export class BrowseTableComponent implements OnInit, OnDestroy {
  @Input() nodes: TreeNode[];
  @Input() readOnly = true;
  @Input() parentNode: TreeNode;
  @Input() userSettings: UserSettings;
  @Input() downloadFiles: Set<string>;
  @Output() downloadFile: EventEmitter<string> = new EventEmitter<string>();
  @Output() uploadFile: EventEmitter<void> = new EventEmitter<void>();
  @Output() deleteFile: EventEmitter<string> = new EventEmitter<string>();
  @Output() filesBatchDownload: EventEmitter<string[]> = new EventEmitter<string[]>();
  filteredNodes: TreeNode[] = null;
  displayedNodes: TreeNode[] = null;
  selectedNodes: TreeNode[] = null;
  searchValue: string = null;
  page = FIRST_PAGE;
  size = DEFAULT_SIZE;
  totalPages: number = null;
  allDisplayedNodesSelected = false;
  getFileSize: (number) => string = getFileSize;
  getDocIconColor: (string) => string = getDocIconColor;
  getDocIcon: (string) => string = getDocIcon;
  readonly iconType = IconType;
  private _destroy$: Subject<void> = new Subject<void>();

  constructor(private sortableService: SortableService, private browseService: BrowseService) {}

  ngOnInit(): void {
    this.sortableService.initialize();

    this.browseService.files$
      .pipe(takeUntil(this._destroy$))
      .pipe(
        map((data: TreeNode[]) => {
          return data.map((item) => ({...item, selected: false}));
        }),
        takeUntil(this._destroy$)
      )
      .subscribe((data: TreeNode[]) => {
        this.page = FIRST_PAGE;
        this.size = DEFAULT_SIZE;
        this.nodes = data;
        this.searchValue = null;
        this.allDisplayedNodesSelected = false;
        this.selectedNodes = null;

        this.totalPages = this.getPagesCount(this.nodes.length, this.size);
        this.filteredNodes = [...this.nodes];
        this.displayedNodes = this.getNodesByPage(this.filteredNodes, this.page, this.size);
      });
  }

  ngOnDestroy(): void {
    this.sortableService.destroy();

    this._destroy$.next();
    this._destroy$.complete();
  }

  onPageChange(page: number): void {
    this.deselectAllDisplayedNodes();

    this.page = page;
    this.displayedNodes = this.getNodesByPage(this.filteredNodes, this.page, this.size);
  }

  onSizeChange(size: number): void {
    this.deselectAllDisplayedNodes();

    this.page = FIRST_PAGE;
    this.size = size;
    this.totalPages = this.getPagesCount(this.filteredNodes.length, this.size);
    this.displayedNodes = this.getNodesByPage(this.filteredNodes, this.page, this.size);
  }

  onSearchValueChange(value: string): void {
    this.deselectAllDisplayedNodes();

    this.searchValue = value;
    this.page = FIRST_PAGE;
    this.filteredNodes = this.filterNodes([...this.nodes], this.searchValue);
    this.totalPages = this.getPagesCount(this.filteredNodes.length, this.size);
    // if total pages les then current page, reset current page to default
    if (this.totalPages < this.page) {
      this.page = FIRST_PAGE;
    }

    this.displayedNodes = this.getNodesByPage(this.filteredNodes, this.page, this.size);
  }

  onDownloadFile(node: TreeNode): void {
    this.downloadFile.emit(node.name);
  }

  onDeleteFile(node: TreeNode): void {
    this.deleteFile.emit(node.name);
  }

  onFileUpload(): void {
    this.uploadFile.emit();
  }

  isSelectAllDisplayedNodesCheckbox(): boolean {
    return !this.displayedNodes?.length;
  }

  onSelectAllDisplayedNodesCheckboxClick(): void {
    if (this.allDisplayedNodesSelected) {
      this.deselectAllDisplayedNodes();
    } else {
      this.selectAllDisplayedNodes();
    }
  }

  isFileCheckboxDisabled(filename: string): boolean {
    return this.downloadFiles.has(filename);
  }

  onFileCheckboxClick(node: TreeNode): void {
    this.updateNodesSelection([node], !node.selected);
  }

  updateSorting({column, sortType}: SortEvent): void {
    this.deselectAllDisplayedNodes();
    this.sortableService.updateActiveSortable(column);

    if (this.filteredNodes.length) {
      if (sortType === SortType.default) {
        this.filteredNodes = this.filterNodes([...this.nodes], this.searchValue);
        this.displayedNodes = this.getNodesByPage(this.filteredNodes, this.page, this.size);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
        this.filteredNodes = this.filteredNodes.sort((a: any, b: any) => {
          return compare(a[column], b[column], sortType);
        });
        this.displayedNodes = this.getNodesByPage(this.filteredNodes, this.page, this.size);
      }
    }
  }

  canShowDeleteButton(): boolean {
    return !this.readOnly;
  }

  canShowUploadButton(): boolean {
    return !this.readOnly;
  }

  isFileDownloading(filename: string): boolean {
    return this.downloadFiles.has(filename);
  }

  isDownloadSelectedButtonDisabled(): boolean {
    return !this.selectedNodes?.length;
  }

  isDownloadSelectedButtonBusy(): boolean {
    return this.browseService.filesBatchDownloading;
  }

  onDownloadSelectedButtonClick(): void {
    this.filesBatchDownload.emit(this.selectedNodes.map((item) => item.name));
  }

  trackByNode(node: TreeNode): string {
    return node.name;
  }

  private updateNodesSelection(nodes: TreeNode[], selection: boolean): void {
    nodes.forEach((node) => {
      node.selected = selection;
    });

    const nodeNames = new Set(nodes.map((item) => item.name));

    this.filteredNodes = this.filteredNodes.map((item) => {
      if (nodeNames.has(item.name)) {
        item = {...item, selected: selection};
      }

      return item;
    });

    this.selectedNodes = this.filteredNodes.filter((item) => item.selected);
    this.allDisplayedNodesSelected = this.selectedNodes.length === this.displayedNodes.length;
  }

  private deselectAllDisplayedNodes(): void {
    if (this.selectedNodes?.length) {
      this.allDisplayedNodesSelected = false;
      this.displayedNodes = this.displayedNodes.map((item) => ({...item, selected: false}));
      this.updateNodesSelection(this.displayedNodes, false);
    }
  }

  private selectAllDisplayedNodes(): void {
    if (!this.selectedNodes?.length || this.selectedNodes?.length !== this.displayedNodes?.length) {
      this.allDisplayedNodesSelected = true;
      this.displayedNodes = this.displayedNodes.map((item) => ({...item, selected: true}));
      this.updateNodesSelection(this.displayedNodes, true);
    }
  }

  private filterNodes(nodes: TreeNode[], value: string): TreeNode[] {
    if (!value?.trim()) {
      return nodes;
    }

    if (!nodes?.length) {
      return [];
    }

    return nodes.filter((node) => node[SEARCHED_FIELD_NAME]?.toLowerCase().includes(value.toLowerCase()));
  }

  private getPagesCount(itemsCount: number, size: number): number {
    return itemsCount && size ? Math.ceil(itemsCount / size) : DEFAULT_TOTAL_PAGE_COUNT;
  }

  private getNodesByPage(nodes: TreeNode[], page: number, size: number): TreeNode[] {
    return nodes.slice((page - 1) * size, page * size);
  }
}
